import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '../../components/feature/SEO';

const disclaimerSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Disclaimer & Regulatory Information | Dulair Private Family Office',
  url: `${import.meta.env.VITE_SITE_URL || 'https://dulair.com'}/disclaimer`,
  description:
    'Regulatory information and disclaimer for Dulair Private Family Office — risk warnings, FCA regulatory status, and financial promotion exemptions for sophisticated investors.',
  lastReviewed: '2025-01-01',
  inLanguage: 'en-GB',
  publisher: {
    '@type': 'Organization',
    name: 'Dulair Private Family Office',
    url: import.meta.env.VITE_SITE_URL || 'https://dulair.com',
  },
};

const DisclaimerPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBackToHome = () => {
    navigate('/', { state: { scrollTo: 'footer' } });
  };

  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="Disclaimer & Regulatory Information | Dulair Private Family Office"
        description="Regulatory information and disclaimer for Dulair Private Family Office — risk warnings, FCA regulatory status, and financial promotion exemptions for sophisticated investors."
        canonical="/disclaimer"
        schema={disclaimerSchema}
      />
      {/* Header */}
      <div className="bg-gradient-to-br from-navy to-gray py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6">
          <button
            onClick={handleBackToHome}
            className="inline-flex items-center gap-2 text-white/70 hover:text-gold transition-colors mb-8 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            <span className="text-sm">Back to Home</span>
          </button>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-serif text-3xl md:text-4xl lg:text-5xl text-white mb-4"
          >
            Disclaimer &amp; Regulatory Information
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-white/70 text-sm md:text-base"
          >
            Last updated: January 2025
          </motion.p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-2xl shadow-sm p-6 md:p-10 space-y-8"
        >
          {/* Important Notice Banner */}
          <div className="bg-gold/10 border border-gold/30 rounded-xl p-5 md:p-6">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-error-warning-line text-gold text-lg"></i>
              </div>
              <div>
                <h3 className="text-navy font-semibold text-sm mb-2">Important Notice</h3>
                <p className="text-text-dark text-sm leading-relaxed">
                  Please read this disclaimer carefully before using this website or engaging with any investment opportunities presented by Dulair Private Family Office. By accessing this website, you acknowledge that you have read, understood, and agree to be bound by the terms set out below.
                </p>
              </div>
            </div>
          </div>

          {/* Section 1 */}
          <section>
            <h4 className="font-serif text-xl md:text-2xl text-navy mb-4">
              <a href="#regulatory-status" id="regulatory-status" className="hover:text-gold transition-colors">1. Regulatory Status</a>
            </h4>
            <p className="text-text-dark text-sm leading-relaxed mb-3">
              Dulair Private Family Office is <strong className="text-navy">not authorised or regulated</strong> by the Financial Conduct Authority (FCA) of the United Kingdom. We are not listed on the FCA Register and do not hold any FCA permissions.
            </p>
            <p className="text-text-dark text-sm leading-relaxed mb-3">
              We do not provide regulated financial advice, investment management services, or any other activity that requires FCA authorisation. Nothing on this website or in any communication from Dulair should be interpreted as a personal recommendation, regulated financial advice, or an invitation to engage in regulated investment activity.
            </p>
            <p className="text-text-dark text-sm leading-relaxed">
              Dulair operates as a private family office and facilitates introductions to investment opportunities on a non-regulated basis. Any investment decisions you make are your own responsibility.
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h4 className="font-serif text-xl md:text-2xl text-navy mb-4">
              <a href="#financial-promotion" id="financial-promotion" className="hover:text-gold transition-colors">2. Financial Promotion Exemptions</a>
            </h4>
            <p className="text-text-dark text-sm leading-relaxed mb-3">
              Certain content on this website may constitute a financial promotion. Where this is the case, such content is communicated in reliance on exemptions available under the <strong className="text-navy">Financial Services and Markets Act 2000 (Financial Promotion) Order 2005</strong> (the &ldquo;FPO&rdquo;), specifically:
            </p>
            <ul className="list-disc list-inside text-text-dark text-sm leading-relaxed space-y-3 ml-2">
              <li>
                <strong className="text-navy">Article 48 &ndash; Certified High Net Worth Individuals:</strong> Persons who have signed a statement confirming that they had, during the financial year immediately preceding the date of the statement, an annual income of not less than &pound;170,000 or held net assets (excluding primary residence, pension rights, and certain insurance contracts) of not less than &pound;430,000.
              </li>
              <li>
                <strong className="text-navy">Article 50 &ndash; Sophisticated Investors:</strong> Persons who have a current certificate from an authorised person confirming they are sufficiently knowledgeable to understand the risks associated with the type of investment being promoted.
              </li>
              <li>
                <strong className="text-navy">Article 50A &ndash; Self-Certified Sophisticated Investors:</strong> Persons who have signed a statement confirming that they meet at least one of the criteria set out in Article 50A of the FPO, such as being a member of a network of business angels, having made more than one investment in an unlisted company in the previous two years, working in private equity, or being a director of a company with an annual turnover of at least &pound;1 million.
              </li>
            </ul>
            <p className="text-text-dark text-sm leading-relaxed mt-3">
              All investors are required to complete a <strong className="text-navy">self-certification process</strong> before being granted access to detailed investment materials or opportunities.
            </p>
          </section>

          {/* Section 3 */}
          <section>
            <h4 className="font-serif text-xl md:text-2xl text-navy mb-4">
              <a href="#risk-warnings" id="risk-warnings" className="hover:text-gold transition-colors">3. Risk Warnings</a>
            </h4>
            <div className="bg-cream rounded-xl p-5 mb-4">
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-alert-line text-red-500 text-base"></i>
                </div>
                <p className="text-text-dark text-sm leading-relaxed font-medium">
                  <strong className="text-navy">Capital at Risk:</strong> The value of investments and the income derived from them can go down as well as up. You may receive back less than your original investment. In some cases, you may lose your entire investment.
                </p>
              </div>
            </div>
            <ul className="list-disc list-inside text-text-dark text-sm leading-relaxed space-y-3 ml-2">
              <li>
                <strong className="text-navy">Past performance</strong> is not a reliable indicator of future results and should not be relied upon when making investment decisions.
              </li>
              <li>
                <strong className="text-navy">Illiquidity risk:</strong> Many investments presented through this website may be illiquid. You may not be able to sell or realise your investment when you wish to do so, or at all.
              </li>
              <li>
                <strong className="text-navy">Property investments</strong> are subject to market fluctuations, changes in rental demand, interest rate movements, and regulatory changes that may adversely affect returns.
              </li>
              <li>
                <strong className="text-navy">Healthcare and venture investments</strong> carry a higher degree of risk, including the possibility of total loss of capital. These investments are typically suitable only for experienced investors who understand and accept such risks.
              </li>
              <li>
                <strong className="text-navy">Tax treatment</strong> depends on individual circumstances and may be subject to change in the future. Tax reliefs and allowances are not guaranteed.
              </li>
              <li>
                <strong className="text-navy">Currency risk:</strong> Where investments are denominated in a currency other than your base currency, exchange rate fluctuations may affect the value of your investment.
              </li>
            </ul>
          </section>

          {/* Section 4 */}
          <section>
            <h4 className="font-serif text-xl md:text-2xl text-navy mb-4">
              <a href="#no-advice" id="no-advice" className="hover:text-gold transition-colors">4. No Investment Advice</a>
            </h4>
            <p className="text-text-dark text-sm leading-relaxed mb-3">
              The information provided on this website is for <strong className="text-navy">general informational purposes only</strong> and does not constitute investment advice, financial advice, trading advice, or any other form of professional advice.
            </p>
            <p className="text-text-dark text-sm leading-relaxed mb-3">
              Dulair does not make any representation or warranty, express or implied, regarding the suitability of any investment for any particular person. You should not treat any content on this website as a recommendation to invest in any particular asset, security, or strategy.
            </p>
            <p className="text-text-dark text-sm leading-relaxed">
              We strongly recommend that you seek <strong className="text-navy">independent professional advice</strong> from a qualified and FCA-authorised financial adviser before making any investment decision. You should also consider seeking independent legal and tax advice where appropriate.
            </p>
          </section>

          {/* Section 5 */}
          <section>
            <h4 className="font-serif text-xl md:text-2xl text-navy mb-4">
              <a href="#no-protection" id="no-protection" className="hover:text-gold transition-colors">5. No Regulatory Protection</a>
            </h4>
            <p className="text-text-dark text-sm leading-relaxed mb-3">
              As Dulair is not authorised or regulated by the FCA, investors do <strong className="text-navy">not</strong> have access to the following protections:
            </p>
            <ul className="list-disc list-inside text-text-dark text-sm leading-relaxed space-y-3 ml-2">
              <li>
                <strong className="text-navy">Financial Services Compensation Scheme (FSCS):</strong> Investments made through or introduced by Dulair are not covered by the FSCS. If Dulair or any associated entity were to fail, you would not be eligible for compensation under this scheme.
              </li>
              <li>
                <strong className="text-navy">Financial Ombudsman Service (FOS):</strong> You will not have access to the Financial Ombudsman Service for the resolution of disputes relating to investments made through Dulair.
              </li>
            </ul>
            <p className="text-text-dark text-sm leading-relaxed mt-3">
              By proceeding with any investment opportunity presented by Dulair, you acknowledge and accept that you will not benefit from these regulatory protections.
            </p>
          </section>

          {/* Section 6 */}
          <section>
            <h4 className="font-serif text-xl md:text-2xl text-navy mb-4">
              <a href="#website-content" id="website-content" className="hover:text-gold transition-colors">6. Website Content &amp; Accuracy</a>
            </h4>
            <p className="text-text-dark text-sm leading-relaxed mb-3">
              While we endeavour to ensure that the information on this website is accurate and up to date, Dulair makes <strong className="text-navy">no warranties or representations</strong> of any kind, express or implied, about the completeness, accuracy, reliability, suitability, or availability of the information, products, services, or related graphics contained on this website.
            </p>
            <p className="text-text-dark text-sm leading-relaxed mb-3">
              Any reliance you place on such information is strictly at your own risk. We reserve the right to modify, update, or remove any content on this website at any time without prior notice.
            </p>
            <p className="text-text-dark text-sm leading-relaxed">
              Projected returns, forecasts, and estimates presented on this website are for illustrative purposes only and are not guaranteed. Actual results may differ materially from any projections or estimates provided.
            </p>
          </section>

          {/* Section 7 */}
          <section>
            <h4 className="font-serif text-xl md:text-2xl text-navy mb-4">
              <a href="#third-party" id="third-party" className="hover:text-gold transition-colors">7. Third-Party Links &amp; Services</a>
            </h4>
            <p className="text-text-dark text-sm leading-relaxed mb-3">
              This website may contain links to third-party websites or services that are not owned or controlled by Dulair. We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party websites or services.
            </p>
            <p className="text-text-dark text-sm leading-relaxed">
              The inclusion of any link does not imply endorsement, approval, or recommendation by Dulair. You access third-party websites entirely at your own risk and subject to the terms and conditions of those websites.
            </p>
          </section>

          {/* Section 8 */}
          <section>
            <h4 className="font-serif text-xl md:text-2xl text-navy mb-4">
              <a href="#limitation" id="limitation" className="hover:text-gold transition-colors">8. Limitation of Liability</a>
            </h4>
            <p className="text-text-dark text-sm leading-relaxed mb-3">
              To the fullest extent permitted by applicable law, Dulair Private Family Office, its directors, officers, employees, agents, and affiliates shall not be liable for any direct, indirect, incidental, special, consequential, or punitive damages, including but not limited to:
            </p>
            <ul className="list-disc list-inside text-text-dark text-sm leading-relaxed space-y-2 ml-2">
              <li>Loss of profits, revenue, or anticipated savings</li>
              <li>Loss of data or business opportunity</li>
              <li>Investment losses or diminution in value</li>
              <li>Any loss arising from reliance on information provided on this website</li>
              <li>Any loss arising from the inability to access or use this website</li>
            </ul>
            <p className="text-text-dark text-sm leading-relaxed mt-3">
              This limitation applies whether the alleged liability is based on contract, tort, negligence, strict liability, or any other basis, even if Dulair has been advised of the possibility of such damage.
            </p>
          </section>

          {/* Section 9 */}
          <section>
            <h4 className="font-serif text-xl md:text-2xl text-navy mb-4">
              <a href="#aml" id="aml" className="hover:text-gold transition-colors">9. Anti-Money Laundering &amp; KYC</a>
            </h4>
            <p className="text-text-dark text-sm leading-relaxed mb-3">
              Dulair is committed to complying with all applicable anti-money laundering (AML) and counter-terrorism financing (CTF) legislation, including the <strong className="text-navy">Money Laundering, Terrorist Financing and Transfer of Funds (Information on the Payer) Regulations 2017</strong>.
            </p>
            <p className="text-text-dark text-sm leading-relaxed">
              We may be required to carry out identity verification and due diligence checks (Know Your Customer / KYC) before entering into any business relationship or facilitating any investment. We reserve the right to refuse to proceed with any transaction or relationship if satisfactory verification cannot be obtained.
            </p>
          </section>

          {/* Section 10 */}
          <section>
            <h4 className="font-serif text-xl md:text-2xl text-navy mb-4">
              <a href="#governing-law" id="governing-law" className="hover:text-gold transition-colors">10. Governing Law &amp; Jurisdiction</a>
            </h4>
            <p className="text-text-dark text-sm leading-relaxed mb-3">
              This disclaimer and any disputes arising out of or in connection with it shall be governed by and construed in accordance with the <strong className="text-navy">laws of England and Wales</strong>.
            </p>
            <p className="text-text-dark text-sm leading-relaxed">
              The courts of England and Wales shall have exclusive jurisdiction to settle any dispute or claim arising out of or in connection with this disclaimer or its subject matter.
            </p>
          </section>

          {/* Section 11 */}
          <section>
            <h4 className="font-serif text-xl md:text-2xl text-navy mb-4">
              <a href="#changes" id="changes" className="hover:text-gold transition-colors">11. Changes to This Disclaimer</a>
            </h4>
            <p className="text-text-dark text-sm leading-relaxed">
              Dulair reserves the right to amend or update this disclaimer at any time without prior notice. Any changes will be effective immediately upon posting on this website. Your continued use of this website following any changes constitutes your acceptance of the revised disclaimer. We encourage you to review this page periodically.
            </p>
          </section>

          {/* Section 12 - Contact */}
          <section>
            <h4 className="font-serif text-xl md:text-2xl text-navy mb-4">
              <a href="#contact-info" id="contact-info" className="hover:text-gold transition-colors">12. Contact Information</a>
            </h4>
            <p className="text-text-dark text-sm leading-relaxed mb-3">
              If you have any questions regarding this disclaimer or require further clarification on our regulatory status, please contact us:
            </p>
            <div className="bg-cream rounded-xl p-5 space-y-2">
              <p className="text-navy text-sm font-medium">Dulair Private Family Office</p>
              <p className="text-text-dark text-sm">Kings Head House, 15 London End</p>
              <p className="text-text-dark text-sm">Beaconsfield, Buckinghamshire, HP9 2HN</p>
              <p className="text-text-dark text-sm">Email: enquiries@dulairpfo.com</p>
              <p className="text-text-dark text-sm">Phone: +44 203 151 0000</p>
            </div>
          </section>

          {/* Related Pages */}
          <section className="border-t border-gray-100 pt-6">
            <h4 className="font-serif text-lg text-navy mb-3">Related Legal Documents</h4>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/privacy-policy"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-cream text-navy rounded-lg hover:bg-gold/15 transition-colors cursor-pointer text-sm font-medium"
              >
                <i className="ri-shield-check-line text-gold"></i>
                Privacy Policy
              </Link>
              <Link
                to="/terms-of-service"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-cream text-navy rounded-lg hover:bg-gold/15 transition-colors cursor-pointer text-sm font-medium"
              >
                <i className="ri-file-text-line text-gold"></i>
                Terms of Service
              </Link>
            </div>
          </section>
        </motion.div>

        {/* Back to Home Button */}
        <div className="mt-8 text-center">
          <button
            onClick={handleBackToHome}
            className="inline-flex items-center gap-2 px-6 py-3 bg-navy text-white rounded-full hover:bg-gold hover:text-navy transition-all duration-300 cursor-pointer text-sm font-medium whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </button>
        </div>
      </div>

      {/* Simple Footer */}
      <footer className="bg-navy py-6">
        <div className="max-w-4xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-white/50 text-xs">
            &copy; 2025 Dulair Private Family Office. All Rights Reserved.
          </p>
          <a
            href="https://readdy.ai/?ref=logo"
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="text-white/40 hover:text-white/60 text-xs transition-colors cursor-pointer"
          >
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
};

export default DisclaimerPage;
